import React, { useContext, useEffect, useState } from 'react';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import FocusTrap from 'focus-trap-react';
import globalStyles from '../DocsBeta/index.module.scss';
import CloseS1 from 'plaid-threads/Icons/CloseS1';
import IconButton from 'src/components/Inputs/IconButton/IconButton';
import Image from 'src/components/Image';

import Context from '../../../contexts/docs';

import styles from './index.module.scss';
import ChatDialog from '../Bill';
import { CHATBOT_EVENTS } from 'src/lib/trackingEvents';
import { useTrack } from 'src/contexts/AnalyticsContext';

// Lazy-loaded so algoliasearch + react-instantsearch (~150-250 KiB) don't ship in
// the initial docs bundle. Gated by `hasEverShownSearch` below so the chunk isn't
// even fetched until the user opens search the first time.
const SearchDialog = dynamic(() => import('../Search/SearchDialog'), {
  ssr: false,
});

enum SearchType {
  SEARCH = 'search',
  CHAT = 'chat',
}

const SearchParent = ({ showFinn = false }) => {
  const { showSearch, dispatch } = useContext(Context);
  const [searchType, setSearchType] = useState(SearchType.SEARCH);
  // Latch flips to true the first time the user opens search and stays true
  // for the lifetime of the page. This keeps SearchDialog mounted across
  // open/close cycles so Algolia is only billed once per page load.
  const [hasEverShownSearch, setHasEverShownSearch] = useState(false);
  const track = useTrack();

  useEffect(() => {
    if (showSearch) {
      setHasEverShownSearch(true);
    }
  }, [showSearch]);

  // If the url contains showChat=true, then pop up Finn by default
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (
      params.get('showChat') === 'true' ||
      params.get('conversation') != null
    ) {
      dispatch({
        type: 'SET_SEARCH_VISIBILITY',
        payload: {
          visibility: true,
          openSearchClicked: true,
        },
      });

      // Update the state value
      setSearchType(SearchType.CHAT);
    }
  }, []);

  const trackTabSelected = () => {
    const payload = { tab: searchType };
    track({ type: CHATBOT_EVENTS.CHAT_SEARCH_TAB_SELECTED, payload: payload });
  };

  useEffect(() => {
    if (showSearch) {
      if (searchType === SearchType.SEARCH) {
        const el = document.querySelector('#search-input');
        if (el instanceof HTMLInputElement) {
          el.focus();
        }
      } else if (searchType === SearchType.CHAT) {
        const el = document.querySelector('#finn-input-div input');
        if (el instanceof HTMLInputElement) {
          el.focus();
        }
      }
      trackTabSelected();
    }
  }, [searchType, showSearch]);

  const handleClick = () =>
    dispatch({
      type: 'SET_SEARCH_VISIBILITY',
      payload: { visibility: false, openSearchClicked: false },
    });

  return (
    <>
      <div className={!showSearch ? styles.hidden : ''}>
        <div
          className={cx(styles.backdrop, showSearch ? styles.show : '')}
          onClick={handleClick}
          role='button'
          tabIndex={-1}
        />

        <FocusTrap
          active={showSearch}
          focusTrapOptions={{
            escapeDeactivates: false,
            initialFocus: false,
            allowOutsideClick: true,
            returnFocusOnDeactivate: true,
          }}
        >
          <div className={cx(globalStyles.docs, styles.parentModalContainer)}>
            <div className={styles.offsetClose}>
              <IconButton
                accessibilityLabel='Close search modal'
                icon={<CloseS1 className={styles.inputIcon} />}
                onClick={() =>
                  dispatch({
                    type: 'SET_SEARCH_VISIBILITY',
                    payload: {
                      visibility: false,
                      openSearchClicked: false,
                    },
                  })
                }
              />
            </div>
            {showFinn && (
              <div className={styles.topBar}>
                <span
                  className={cx(
                    styles.barLabel,
                    searchType === SearchType.SEARCH && styles.selected,
                  )}
                >
                  <button
                    className={styles.barButton}
                    onClick={(e) => setSearchType(SearchType.SEARCH)}
                  >
                    Search the docs
                  </button>
                </span>
                <span
                  className={cx(
                    styles.barLabel,
                    searchType === SearchType.CHAT && styles.selected,
                  )}
                >
                  <button
                    className={styles.barButton}
                    onClick={(e) => setSearchType(SearchType.CHAT)}
                  >
                    Ask Bill!
                  </button>
                  <Image
                    src='/assets/img/docs/bill-happy.png'
                    alt='Ask Bill!'
                    isLazy={false}
                    className={styles.billIcon}
                    width={25}
                    height={25}
                  />
                </span>
              </div>
            )}
            {hasEverShownSearch && (
              <SearchDialog
                isVisible={showSearch && searchType === SearchType.SEARCH}
              />
            )}
            {showFinn && (
              <ChatDialog
                isVisible={showSearch && searchType === SearchType.CHAT}
              />
            )}
          </div>
        </FocusTrap>
      </div>
    </>
  );
};

SearchParent.displayName = 'SearchParent';
export default SearchParent;
